// The Book panel's request bodies and rules as pure functions (fix round 2; three setter links 2026-10-11). Unit-tested
// in tests/fd-dialer.test.ts and tests/fd-book-dispensary.test.ts. Which link a lead may book is the shared rule in
// src/shared/calendly.ts (bookingOptions / resolveBookingSlug): the server applies the same one.
import { isCannabisNiche } from '../../../src/shared/offer-guards';
import { ADD_TO_CALENDAR_LINE, linkSendsTexts, setterLink } from '../../../src/shared/calendly';
import type { MeetingBookRequestCore } from './types';

/** A cannabis booking: the business's niche or the campaign's is cannabis (offer-guards.ts). It books /your-store only,
 *  and our own Text button never texts it; Calendly's reminder text is allowed (PJ 2026-10-11). The server also checks
 *  every campaign the business is in. */
export function dispensaryBooking(niches: ReadonlyArray<string | null | undefined>): boolean {
  return niches.some(isCannabisNiche);
}

/**
 * The reminder-text box (PJ 2026-10-11: one Calendly text, 2 hours before, per booking): offered on every link with a
 * Calendly text workflow (the three setter links), for the US, Canada and cannabis alike. The setter asks "What's the
 * best cell for a reminder text before the call?" and ticks it only when they give one.
 */
export function textReminderOffered(eventSlug: string | null | undefined): boolean {
  return linkSendsTexts(eventSlug);
}

export interface BookForm {
  startTime: string;
  name: string;
  email: string;
  mobileE164: string | null;   // normalised by toE164Nanp; null = none given
  bookingSystem: string;       // the link's first select ('' = not asked)
  afterFive: string;           // the link's second select ('' = not asked: nothing is sent for it)
  note: string;
  textOk: boolean;             // the reminder-text box (only shown on a link that texts)
  eventSlug: string;           // "Book into": the link chosen in the panel
}

/** POST /api/meetings/book body. textReminderOk only with a mobile, the box ticked and a link that texts. */
export function buildBookRequest(membershipId: number, callId: number | null, f: BookForm): MeetingBookRequestCore {
  return {
    membershipId,
    callId: callId ?? undefined,
    startTime: f.startTime,
    name: f.name.trim(),
    email: f.email.trim(),
    mobile: f.mobileE164 ?? undefined,
    bookingSystem: f.bookingSystem || undefined,
    afterFive: f.afterFive || undefined,
    note: f.note.trim() || undefined,
    textReminderOk: textReminderOffered(f.eventSlug) && f.textOk && !!f.mobileE164,
    eventSlug: f.eventSlug,
  };
}

/** The panel's say-back line for the chosen link, then the add-to-calendar line (APPROVAL-three-links 6.8). */
export function sayBackLines(eventSlug: string | null | undefined, when: string): string[] {
  const link = setterLink(eventSlug);
  const first = link
    ? link.sayBack.replace('{when}', when)
    : `You're booked for ${when} your time with one of our specialists on Google Meet. You'll get an email from Valistix with the link.`;
  return [first, ADD_TO_CALENDAR_LINE];
}
